import { ModuleTypeEnum } from '../types/module-type'

/**
 * Header knowledge shared by every import surface.
 *
 * The browser wizard (`apps/web/src/lib/csv-utils.ts` → `createFieldMappings`)
 * and the server auto-mapper (`apps/api/src/import/utils/import-auto-map.ts`,
 * used by Benny's import tools and, later, source connectors) used to carry
 * their own copies of these tables and drifted. Both now read them from here,
 * so a HubSpot, Jira, Harvest or Toggl export maps the same way through either
 * path. The parity fixture in `header-parity-cases.ts` is asserted by both
 * surfaces' test suites.
 *
 * Every synonym is stored in NORMALIZED form (see `normalizeImportHeader`):
 * lowercase, alphanumerics only.
 */

/** Lowercase, trimmed, alphanumerics only — "Billable?" → "billable". */
export function normalizeImportHeader(header: string): string {
  return header
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, '')
}

/**
 * Headers that route legacy "Status"/"Stage" columns on contact- and
 * company-shaped files to the lifecycle stage. The wizard registers them in
 * its hand-tuned pass (lifecycleStage first, relationshipTypes only for keys
 * left over); the server registers them as ordinary synonyms.
 */
export const LIFECYCLE_HEADER_ALIASES: readonly string[] = [
  'standing',
  'standingstage',
  'lifecycle',
  'lifecyclestage',
  'stage',
  'status',
]

/**
 * Generic header synonyms (target field name → normalized header spellings).
 * Covers the headers real exports use — HubSpot, Google/Outlook contacts,
 * Excel sheets. Only applied when the target field exists on the module, so a
 * synonym for a field the module lacks is harmless.
 */
export const IMPORT_HEADER_SYNONYMS: Readonly<
  Record<string, readonly string[]>
> = {
  firstName: ['first', 'firstname', 'givenname', 'forename'],
  lastName: ['last', 'lastname', 'surname', 'familyname'],
  email: ['emailaddress', 'workemail', 'primaryemail', 'mail', 'emailaddress1'],
  phone: [
    'phonenumber',
    'mobile',
    'mobilephone',
    'telephone',
    'tel',
    'cell',
    'cellphone',
    'workphone',
    'primaryphone',
  ],
  website: [
    'url',
    'website',
    'websiteurl',
    'web',
    'domain',
    'homepage',
    'site',
  ],
  description: ['notes', 'note', 'details', 'comments', 'comment', 'about'],
  dueDate: ['deadline', 'due', 'enddate', 'targetdate', 'finishdate'],
  startDate: ['start', 'begindate', 'kickoff'],
  closeDate: ['closedate', 'expectedclosedate', 'closingdate'],
  amount: ['value', 'dealvalue', 'dealamount', 'price', 'revenue'],
  jobTitle: ['jobtitle', 'position'],
  tags: ['labels', 'label', 'tag', 'categories', 'category'],
  assignees: ['owner', 'assignedto', 'dealowner', 'taskowner'],
  city: ['town'],
  postalCode: ['zip', 'zipcode', 'postcode'],
  lifecycleStage: LIFECYCLE_HEADER_ALIASES,
}

const COMPANY_NAME_HEADERS = [
  'company',
  'companyname',
  'organisation',
  'organization',
  'businessname',
] as const

const PERSON_EMPLOYER_HEADERS = [
  'company',
  'organisation',
  'organization',
  'employer',
  'account',
  'accountname',
] as const

const PERSON_NAME_HEADERS = [
  'fullname',
  'contactname',
  'displayname',
  'contact',
  'lead',
  'leadname',
] as const

/**
 * Per-module synonyms. "Company" is the record's NAME for company-shaped
 * modules but a related organization for people-shaped ones; "Title" is the
 * record name for work items but the job title for contacts. Module synonyms
 * are consulted before the generic ones.
 */
export const IMPORT_MODULE_HEADER_SYNONYMS: Readonly<
  Partial<Record<ModuleTypeEnum, Readonly<Record<string, readonly string[]>>>>
> = {
  [ModuleTypeEnum.ENTITY]: {
    name: [...COMPANY_NAME_HEADERS, 'account', 'accountname', 'clientname'],
  },
  [ModuleTypeEnum.SUPPLIER]: {
    name: [...COMPANY_NAME_HEADERS, 'suppliername', 'vendor', 'vendorname'],
  },
  [ModuleTypeEnum.DEAL]: {
    name: ['dealname', 'opportunity', 'opportunityname', 'title'],
  },
  [ModuleTypeEnum.CONTACT]: {
    // Contacts are people: "Company" is the employer (companyName), and
    // "Title"/"Role" mean job title — never a record name. A bare "Contact" or
    // "Lead" column in a contacts file IS the person's name — it maps to the
    // full-name splitter.
    name: PERSON_NAME_HEADERS,
    companyName: PERSON_EMPLOYER_HEADERS,
    title: ['jobtitle', 'position', 'role'],
    supplier: ['vendor', 'vendorname', 'suppliername'],
  },
  [ModuleTypeEnum.LEAD]: {
    // Leads import through the contact pipeline; same person-name logic.
    name: PERSON_NAME_HEADERS,
    companyName: PERSON_EMPLOYER_HEADERS,
    title: ['jobtitle', 'position', 'role'],
  },
  [ModuleTypeEnum.TASK]: {
    // "Summary" is Jira's task title; "Task Name"/"Task" are the common
    // spreadsheet headings.
    name: ['title', 'summary', 'taskname', 'task'],
    // "Issue Type" is deliberately absent: the importer reads it to detect
    // Jira Epics, and consuming it as the task type would break that.
    taskType: ['tasktype', 'type', 'worktype', 'worktypename', 'tasktypename'],
    group: ['taskgroup', 'epic', 'epicname'],
    parentTaskId: ['parentkey', 'parenttask', 'parent'],
  },
  [ModuleTypeEnum.PROJECT]: {
    name: ['title', 'projectname'],
  },
  [ModuleTypeEnum.TIME_ENTRY]: {
    // Who logged the time. Harvest, Toggl and Clockify detailed exports all
    // carry an email column, which resolves more reliably than a name.
    member: [
      'user',
      'useremail',
      'memberemail',
      'membername',
      'person',
      'email',
      'emailaddress',
    ],
    // Harvest's detailed export splits the person into two columns; the
    // importer joins them back into one member reference.
    memberFirstName: ['firstname', 'userfirstname', 'memberfirstname'],
    memberLastName: ['lastname', 'userlastname', 'memberlastname'],
    // Toggl/Clockify stamp each entry with its start; the calendar day of the
    // start is the entry date.
    entryDate: ['date', 'spentdate', 'startdate', 'day', 'workdate'],
    project: ['projectname', 'projectcode'],
    task: ['taskname', 'tasknumber', 'issuekey'],
    duration: [
      'hours',
      'timespent',
      'hoursworked',
      'hoursspent',
      'loggedhours',
      'workedhours',
      // Clockify's decimal column. Its "Duration (h)" twin is clock format,
      // which the importer also reads; the decimal one is preferred.
      'durationdecimal',
      'durationh',
    ],
    billable: ['isbillable'],
  },
}

/**
 * Normalized synonyms for one target field: module-specific first, then the
 * generic table. Callers decide whether the field exists on the module.
 */
export function importSynonymsFor(
  moduleType: ModuleTypeEnum | undefined,
  fieldName: string,
): string[] {
  const moduleSynonyms =
    (moduleType && IMPORT_MODULE_HEADER_SYNONYMS[moduleType]?.[fieldName]) || []
  return [...moduleSynonyms, ...(IMPORT_HEADER_SYNONYMS[fieldName] ?? [])]
}

/**
 * Value-shape detectors for headers nothing else matched — a column of
 * "x@y.com" values is an email even when its header is "Column 3". Each
 * detector claims its target at most once per file.
 */
export const IMPORT_VALUE_DETECTORS: readonly {
  targetNames: readonly string[]
  test: (value: string) => boolean
}[] = [
  {
    targetNames: ['email'],
    test: value => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value),
  },
  {
    targetNames: ['website'],
    test: value => /^(https?:\/\/|www\.)[^\s]+\.[^\s]{2,}$/i.test(value),
  },
  {
    targetNames: ['phone'],
    test: value => /^[+()\d][\d\s().+-]{6,19}$/.test(value.trim()),
  },
]
